import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { messagesApi } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Send, Loader2, MessageSquare, Search } from 'lucide-react';
import { formatDateTime, getInitials } from '../lib/utils';

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchThreads();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      fetchMessages(selectedThread.thread_id);
    }
  }, [selectedThread]);

  const fetchThreads = async () => {
    try {
      const response = await messagesApi.getThreads();
      setThreads(response.data);
      if (response.data.length > 0 && !selectedThread) {
        setSelectedThread(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      const response = await messagesApi.getThread(threadId);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedThread) return;

    setSending(true);
    try {
      await messagesApi.send({
        recipient_id: selectedThread.other_user?.id,
        booking_id: selectedThread.last_message?.booking_id,
        content: newMessage,
      });
      setNewMessage('');
      fetchMessages(selectedThread.thread_id);
      fetchThreads();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const filteredThreads = threads.filter((thread) =>
    thread.other_user?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-farmhouse-charcoal" data-testid="messages-page">
      <div className="max-w-6xl mx-auto h-[calc(100vh-4rem)]">
        <div className="flex h-full">
          {/* Threads Sidebar */}
          <div className="w-80 border-r border-farmhouse-subtle bg-farmhouse-paper flex flex-col">
            <div className="p-4 border-b border-farmhouse-subtle">
              <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-3">Messages</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-farmhouse-muted" />
                <Input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="form-input pl-9 h-10"
                />
              </div>
            </div>

            <ScrollArea className="flex-1">
              {filteredThreads.length > 0 ? (
                filteredThreads.map((thread) => (
                  <button
                    key={thread.thread_id}
                    onClick={() => setSelectedThread(thread)}
                    className={`w-full p-4 flex items-start gap-3 border-b border-farmhouse-subtle transition-colors ${
                      selectedThread?.thread_id === thread.thread_id
                        ? 'bg-farmhouse-saddle/10'
                        : 'hover:bg-farmhouse-subtle/50'
                    }`}
                    data-testid={`thread-${thread.thread_id}`}
                  >
                    <div className="avatar shrink-0 bg-farmhouse-saddle text-white text-sm">
                      {getInitials(thread.other_user?.name)}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-farmhouse-cream truncate">
                          {thread.other_user?.name || 'Unknown'}
                        </p>
                        {thread.unread_count > 0 && (
                          <span className="shrink-0 w-5 h-5 rounded-full bg-farmhouse-saddle text-white text-xs flex items-center justify-center">
                            {thread.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-farmhouse-muted truncate mt-1">
                        {thread.last_message?.content}
                      </p>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-farmhouse-muted">
                  No conversations found
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Messages Area */}
          <div className="flex-1 flex flex-col bg-farmhouse-charcoal">
            {selectedThread ? (
              <>
                {/* Header */}
                <div className="p-4 border-b border-farmhouse-subtle bg-farmhouse-paper">
                  <div className="flex items-center gap-3">
                    <div className="avatar bg-farmhouse-saddle text-white">
                      {getInitials(selectedThread.other_user?.name)}
                    </div>
                    <div>
                      <p className="font-medium text-farmhouse-cream">
                        {selectedThread.other_user?.name}
                      </p>
                      <p className="text-sm text-farmhouse-muted">
                        {selectedThread.last_message?.booking_id && 'Re: Booking inquiry'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`message-bubble ${
                            message.sender_id === user?.id ? 'sent' : 'received'
                          }`}
                        >
                          <p>{message.content}</p>
                          <p className={`text-xs mt-1 ${
                            message.sender_id === user?.id ? 'text-white/70' : 'text-farmhouse-muted'
                          }`}>
                            {formatDateTime(message.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-farmhouse-subtle bg-farmhouse-paper">
                  <div className="flex gap-3">
                    <Input
                      type="text"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="form-input flex-1"
                      data-testid="message-input"
                    />
                    <Button
                      type="submit"
                      className="btn-primary"
                      disabled={sending || !newMessage.trim()}
                      data-testid="send-message"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-farmhouse-subtle" />
                  <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-2">
                    No conversation selected
                  </h3>
                  <p className="text-farmhouse-muted">
                    Select a conversation to view messages
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
